import { CSSProperties, createElement, useCallback, useEffect, useRef } from 'react'

import { useToast } from '@verbit-ai/verbit-ui-library'

import { useRefreshMetadata } from 'src/contexts/Transcript/MetadataContext'
import { useTranscriptClient, useTranscriptConnectionScope, useTranscriptConnectionState } from 'src/contexts/Transcript/TranscriptContext'
import { useTranscriptError } from 'src/contexts/Transcript/TranscriptErrorContext'
import { useIsOnline } from 'src/hooks/useIsOnline'
import { usePrevious } from 'src/hooks/usePrevious'
import { useToastWithAnnouncement } from 'src/hooks/useToastWithAnnouncement'
import { TranscriptError } from 'src/models/Error'
import { isUserSpeaker, redirectToLogin } from 'src/utils/env'

const ERROR_DISPLAY_TIMEOUT_MS = 3_000
const RECONNECTED_SUCCESS_TOAST_DURATION_MS = 10_000
const DISCONNECTED_WARNING_TOAST_ID = 'transcript-connection-disconnected-warning'
const RECONNECTED_SUCCESS_TOAST_ID = 'transcript-connection-reconnected-success'
const TOAST_DESCRIPTION_STYLE: CSSProperties = { wordBreak: 'normal', overflowWrap: 'anywhere' }

export const useConnectionListener = () => {
    const { transcriptError, setTranscriptError } = useTranscriptError()
    const toast = useToast()
    const showToast = useToastWithAnnouncement()
    const errorDisplayTimeout = useRef<NodeJS.Timeout | null>(null)
    const disconnectedWarningShownRef = useRef(false)
    const isOnline = useIsOnline()
    const transcriptClient = useTranscriptClient()
    const transcriptConnectionScope = useTranscriptConnectionScope()
    const transcriptConnectionState = useTranscriptConnectionState()
    const refreshMetadata = useRefreshMetadata()
    const hasBeenConnectedOnceRef = useRef(transcriptConnectionState.status === 'connected' && transcriptConnectionState.isInitialized)
    // `'terminated'` is a deliberate server-side end (not a connectivity failure), so
    // it is excluded here to suppress the interrupted/reconnected toast transitions.
    const isConnectivityLost =
        !isOnline || (transcriptConnectionState.status !== 'connected' && transcriptConnectionState.status !== 'terminated')
    const previousIsConnectivityLost = usePrevious(isConnectivityLost)
    const previousTranscriptConnectionScope = usePrevious(transcriptConnectionScope)
    const isTranscriptConnectionScopeChanged =
        previousTranscriptConnectionScope !== undefined && previousTranscriptConnectionScope !== transcriptConnectionScope

    const closeConnectionToasts = useCallback(() => {
        toast.close(DISCONNECTED_WARNING_TOAST_ID)
        toast.close(RECONNECTED_SUCCESS_TOAST_ID)
    }, [toast])

    const showDisconnectedWarningToast = useCallback(() => {
        if (disconnectedWarningShownRef.current) {
            return
        }

        toast.close(RECONNECTED_SUCCESS_TOAST_ID)
        showToast({
            id: DISCONNECTED_WARNING_TOAST_ID,
            type: 'network',
            title: 'Your connection was interrupted',
            description: createElement(
                'span',
                { style: TOAST_DESCRIPTION_STYLE },
                "Some transcript updates may be delayed. We'll catch up automatically once the connection is restored.",
            ),
            duration: null,
        })
        disconnectedWarningShownRef.current = true
    }, [showToast, toast])

    const clearDisconnectedWarningTimeout = useCallback(() => {
        if (!errorDisplayTimeout.current) {
            return
        }

        clearTimeout(errorDisplayTimeout.current)
        errorDisplayTimeout.current = null
    }, [])

    const scheduleDisconnectedWarningToast = useCallback(() => {
        if (disconnectedWarningShownRef.current || errorDisplayTimeout.current) {
            return
        }

        errorDisplayTimeout.current = setTimeout(() => {
            errorDisplayTimeout.current = null
            showDisconnectedWarningToast()
        }, ERROR_DISPLAY_TIMEOUT_MS)
    }, [showDisconnectedWarningToast])

    const showReconnectedSuccessToast = useCallback(() => {
        if (!disconnectedWarningShownRef.current) {
            return
        }

        toast.close(DISCONNECTED_WARNING_TOAST_ID)
        showToast({
            id: RECONNECTED_SUCCESS_TOAST_ID,
            type: 'success',
            title: 'Connection restored',
            description: createElement('span', { style: TOAST_DESCRIPTION_STYLE }, 'The transcript will be up to date shortly.'),
            duration: RECONNECTED_SUCCESS_TOAST_DURATION_MS,
        })
        disconnectedWarningShownRef.current = false
    }, [showToast, toast])

    const resetTransientConnectionUxState = useCallback(() => {
        clearDisconnectedWarningTimeout()
        closeConnectionToasts()
        disconnectedWarningShownRef.current = false
    }, [clearDisconnectedWarningTimeout, closeConnectionToasts])

    useEffect(() => {
        if (!isTranscriptConnectionScopeChanged) {
            return
        }

        resetTransientConnectionUxState()
        hasBeenConnectedOnceRef.current = false
    }, [isTranscriptConnectionScopeChanged, resetTransientConnectionUxState])

    useEffect(() => {
        if (transcriptConnectionState.status !== 'connected' || !transcriptConnectionState.isInitialized) {
            return
        }
        if (hasBeenConnectedOnceRef.current) {
            return
        }

        hasBeenConnectedOnceRef.current = true
    }, [transcriptConnectionState.isInitialized, transcriptConnectionState.status])

    useEffect(() => {
        if (transcriptConnectionState.status !== 'failed') {
            return
        }

        resetTransientConnectionUxState()

        switch (transcriptConnectionState.error) {
            case 'auth_expired':
                redirectToLogin()

                return
            case 'access_denied':
                setTranscriptError(TranscriptError.ACCESS_DENIED)

                return
            case 'not_found':
                setTranscriptError(TranscriptError.TRANSCRIPT_NOT_FOUND)

                return
            case 'server_overload':
                setTranscriptError(TranscriptError.SERVER_OVERLOAD)

                return
            case 'server_error':
                setTranscriptError(TranscriptError.TECHNICAL_DIFFICULTIES)

                return
            default:
                setTranscriptError(TranscriptError.CONNECTION_ERROR)

                return
        }
    }, [resetTransientConnectionUxState, setTranscriptError, transcriptConnectionState.error, transcriptConnectionState.status])

    useEffect(() => {
        // `'terminated'` has its own dedicated effect below; skip here to avoid
        // firing a spurious "Connection restored" toast when 4200 arrives mid-disconnect.
        const shouldSkipConnectivityTransitionHandling =
            isTranscriptConnectionScopeChanged ||
            transcriptConnectionState.status === 'failed' ||
            transcriptConnectionState.status === 'terminated'

        if (shouldSkipConnectivityTransitionHandling) {
            return
        }

        if (!hasBeenConnectedOnceRef.current) {
            return
        }

        if (previousIsConnectivityLost === false && isConnectivityLost) {
            scheduleDisconnectedWarningToast()
        }

        if (previousIsConnectivityLost === true && !isConnectivityLost) {
            clearDisconnectedWarningTimeout()

            if (transcriptClient.source === 'ably' && !isUserSpeaker()) {
                window.location.reload()

                return
            }

            showReconnectedSuccessToast()
        }

        if (transcriptConnectionState.status === 'connected' && transcriptError === TranscriptError.CONNECTION_ERROR) {
            setTranscriptError(null)
        }
    }, [
        clearDisconnectedWarningTimeout,
        isTranscriptConnectionScopeChanged,
        isConnectivityLost,
        previousIsConnectivityLost,
        scheduleDisconnectedWarningToast,
        setTranscriptError,
        showReconnectedSuccessToast,
        toast,
        transcriptClient,
        transcriptConnectionScope,
        transcriptConnectionState.status,
        transcriptError,
    ])

    // TODO(LG-1762): remove along with the `'terminated'` status when editing features go live.
    useEffect(() => {
        if (transcriptConnectionState.status !== 'terminated') {
            return
        }

        // Clear any lingering interrupted-connection UX (e.g. user was mid-disconnect
        // when the server sent 4200), then re-sync metadata so derived UI (session
        // finished state, concluded toast, etc.) reflects the server truth immediately
        // rather than waiting up to `METADATA_REFRESH_TIMEOUT` for the next poll.
        resetTransientConnectionUxState()
        refreshMetadata().catch(() => undefined)
    }, [transcriptConnectionState.status, resetTransientConnectionUxState, refreshMetadata])

    useEffect(() => {
        return () => {
            clearDisconnectedWarningTimeout()
        }
    }, [clearDisconnectedWarningTimeout])
}
